'use client';

import { useState } from 'react';
import { EyeIcon, EyeOffIcon } from '../common/Icons';

type Mode = 'login' | 'register';

interface Props {
  mode: Mode;
  loading: boolean;
  form: { name: string; email: string; password: string };
  setField: (field: 'name' | 'email' | 'password') => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

function UnderlineInput({ id, type, placeholder, value, onChange, required }: {
  id: string; type: string; placeholder: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; required?: boolean;
}) {
  return (
    <div className="border-b border-gray-300 pb-1 focus-within:border-[#0b57d0] transition-colors">
      <input
        id={id} type={type} required={required} placeholder={placeholder} value={value} onChange={onChange}
        className="w-full bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none py-1"
      />
    </div>
  );
}

export function AuthForm({ mode, loading, form, setField, onSubmit }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const submitLabel = loading ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account';

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {mode === 'register' && (
        <UnderlineInput id="name" type="text" placeholder="Name" value={form.name} onChange={setField('name')} required />
      )}
      <UnderlineInput id="email" type="email" placeholder="Email" value={form.email} onChange={setField('email')} required />

      <div className="border-b border-gray-300 pb-1 focus-within:border-[#0b57d0] transition-colors flex items-center gap-2">
        <input
          id="password"
          type={showPassword ? 'text' : 'password'}
          required
          placeholder="Password"
          value={form.password}
          onChange={setField('password')}
          className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none py-1"
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          tabIndex={-1}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 rounded-full text-sm font-semibold text-white transition-opacity disabled:opacity-60"
        style={{ backgroundColor: 'var(--color-primary)' }}
      >
        {submitLabel}
      </button>

      {mode === 'register' && (
        <p className="text-center text-xs text-gray-400 leading-relaxed">
          By creating an account you agree to our{' '}
          <a href="/terms" className="text-[#0b57d0] hover:underline" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-[#0b57d0] hover:underline" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      )}
    </form>
  );
}
